import { useEffect, useState } from "react";
import axios from "axios";
import cn from "classnames";
import type { RankedOption, SurveyAggregates } from "../../common/postTrialSurvey";
import Reveal from "../common/Reveal";
import AgencySurveyTab from "./AgencySurveyTab";

type AgencyRow = {
  agency_id: string;
  agency_name: string;
  responses: number;
  avg_overall: number | null;
  nps: number | null;
  pct_keep: number | null;
  pct_quality_better: number | null;
  weighted_avg_min: number | null;
  pct_skeptics_won_over: number | null;
  last_completed_at: string | null;
};

type GlobalSurvey = {
  aggregates: SurveyAggregates;
  agencies: number;
  byAgency: AgencyRow[];
  quotes: {
    text: string;
    name: string | null;
    rank: string | null;
    agency_id: string;
    agency_name: string;
    completed_at: string | null;
  }[];
};

const fmt = (n: number | null, suffix = "") => (n === null ? "—" : `${n}${suffix}`);

function Tile({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-xl border border-base-300 bg-base-200/50 p-4">
      <div className="text-[11px] uppercase tracking-wider font-semibold text-base-content/50">{label}</div>
      <div className="mt-1 text-2xl font-bold tabular-nums text-base-content">{value}</div>
      {sub && <div className="text-[11px] text-base-content/50 mt-0.5">{sub}</div>}
    </div>
  );
}

function BarGroup({ title, options, otherTexts }: { title: string; options: RankedOption[]; otherTexts?: string[] }) {
  const shown = options.filter((o) => o.count > 0);
  const max = Math.max(0, ...shown.map((o) => o.count));
  return (
    <div className="rounded-xl border border-base-300 p-4">
      <h4 className="text-sm font-semibold text-base-content mb-3">{title}</h4>
      {shown.length === 0 ? (
        <p className="text-xs text-base-content/50">No answers yet.</p>
      ) : (
        <div className="space-y-2">
          {shown.map((o) => (
            <div key={o.value} className="flex items-center gap-3 text-sm">
              <div className="w-56 shrink-0 truncate text-base-content/80" title={o.label}>
                {o.label}
              </div>
              <div className="flex-1 h-2 rounded-full bg-base-300 overflow-hidden">
                <div className="h-full bg-blue-500 rounded-full" style={{ width: `${max ? Math.round((o.count / max) * 100) : 0}%` }} />
              </div>
              <div className="w-20 shrink-0 text-right tabular-nums text-base-content/60">
                {o.count} · {o.pct}%
              </div>
            </div>
          ))}
        </div>
      )}
      {otherTexts && otherTexts.length > 0 && (
        <ul className="mt-3 space-y-1 border-t border-base-300 pt-3 text-xs text-base-content/70">
          {otherTexts.map((t, i) => (
            <li key={i}>“{t}”</li>
          ))}
        </ul>
      )}
    </div>
  );
}

// Staff-portal roll-up of every agency's post-trial survey: the same
// aggregates as the per-agency tab, computed across all completed
// responses, plus a per-agency table and every quote. Clicking an agency
// swaps the panel to just that agency's results (the same view as the
// Officer Survey tab in the edit modal).
const SurveyResultsPanel: React.FC<{ adminEmail: string }> = ({ adminEmail }) => {
  const [data, setData] = useState<GlobalSurvey | null>(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [showAllQuotes, setShowAllQuotes] = useState(false);
  const [selected, setSelected] = useState<AgencyRow | null>(null);

  useEffect(() => {
    axios
      .get<GlobalSurvey>("/api/agency-management/survey")
      .then((r) => setData(r.data))
      .catch((err) => console.error("Failed to load survey results:", err))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !data) return null;
  const a = data.aggregates;
  const quotes = showAllQuotes ? data.quotes : data.quotes.slice(0, 6);

  if (selected) {
    return (
      <div className="mt-8 rounded-xl border border-base-300 bg-base-100 p-4">
        <button
          type="button"
          onClick={() => setSelected(null)}
          className="mb-4 text-xs font-medium text-blue-600 dark:text-blue-300 hover:underline"
        >
          ← All agencies
        </button>
        <AgencySurveyTab key={selected.agency_id} agencyId={selected.agency_id} adminEmail={adminEmail} title={`${selected.agency_name} · survey results`} />
      </div>
    );
  }

  return (
    <div className="mt-8 rounded-xl border border-base-300 bg-base-100 p-4">
      <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
        <div>
          <h2 className="text-lg font-semibold text-base-content mb-1">Post-trial survey results</h2>
          <p className="text-xs text-base-content/60">
            Every completed officer survey across {data.agencies} {data.agencies === 1 ? "agency" : "agencies"}. Percentages are of officers who answered each question.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="text-xs font-medium text-blue-600 dark:text-blue-300 hover:underline"
        >
          {expanded ? "Hide details" : "Show details"}
        </button>
      </div>

      {a.responses === 0 ? (
        <p className="text-sm text-base-content/60">No completed surveys yet.</p>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
            <Tile label="Responses" value={String(a.responses)} sub={`${data.agencies} ${data.agencies === 1 ? "agency" : "agencies"}`} />
            <Tile label="Avg rating" value={fmt(a.avg_overall, " / 5")} />
            <Tile label="NPS" value={fmt(a.nps.score)} sub={`${a.nps.promoters} promoters · ${a.nps.detractors} detractors`} />
            <Tile label="Would keep it" value={fmt(a.keep.pct_yes_or_probably, "%")} />
            <Tile label="Reports better" value={fmt(a.pct_quality_better, "%")} />
            <Tile label="Saved per report" value={fmt(a.time_saved.weighted_avg_min, " min")} />
            <Tile label="Less overtime" value={fmt(a.pct_overtime_reduced, "%")} />
            <Tile
              label="Skeptics won over"
              value={a.ai_attitude.skeptics > 0 ? `${a.ai_attitude.skeptics_won_over} of ${a.ai_attitude.skeptics}` : "—"}
              sub={a.ai_attitude.pct_open_before !== null && a.ai_attitude.pct_open_after !== null ? `open to AI ${a.ai_attitude.pct_open_before}% → ${a.ai_attitude.pct_open_after}%` : undefined}
            />
          </div>

          <div className="mt-4 rounded-xl border border-base-300 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-base-200/60 text-[11px] uppercase tracking-wider text-base-content/50">
                <tr>
                  <th className="text-left px-4 py-2 font-semibold">Agency</th>
                  <th className="text-right px-4 py-2 font-semibold">Responses</th>
                  <th className="text-right px-4 py-2 font-semibold">Rating</th>
                  <th className="text-right px-4 py-2 font-semibold">NPS</th>
                  <th className="text-right px-4 py-2 font-semibold">Keep</th>
                  <th className="text-right px-4 py-2 font-semibold">Better</th>
                  <th className="text-right px-4 py-2 font-semibold">Min saved</th>
                  <th className="text-right px-4 py-2 font-semibold">Skeptics won</th>
                  <th className="text-right px-4 py-2 font-semibold">Last</th>
                </tr>
              </thead>
              <tbody>
                {data.byAgency.map((row, i) => (
                  <Reveal
                    as="tr"
                    key={row.agency_id}
                    index={i}
                    className={cn("border-t border-base-300", "cursor-pointer hover:bg-base-200/60")}
                    onClick={() => setSelected(row)}
                    title="View this agency's results"
                  >
                    <td className="px-4 py-2 font-medium text-base-content">{row.agency_name}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{row.responses}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{fmt(row.avg_overall)}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{fmt(row.nps)}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{fmt(row.pct_keep, "%")}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{fmt(row.pct_quality_better, "%")}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{fmt(row.weighted_avg_min)}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{fmt(row.pct_skeptics_won_over, "%")}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-base-content/60">
                      {row.last_completed_at ? new Date(row.last_completed_at).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "—"}
                    </td>
                  </Reveal>
                ))}
              </tbody>
            </table>
          </div>

          {expanded && (
            <>
              <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
                <BarGroup title="Feelings about AI before the trial" options={a.ai_attitude.before} />
                <BarGroup title="Feelings about AI after the trial" options={a.ai_attitude.after} />
                <BarGroup title="Time saved per report" options={a.time_saved.buckets} />
                <BarGroup title="Most useful features" options={a.top_features} />
                <BarGroup title="What improved about reports" options={a.per_question.quality_improvements?.distribution ?? []} otherTexts={a.per_question.quality_improvements?.other_texts} />
                <BarGroup title="Where the time went" options={a.time_reinvested} otherTexts={a.per_question.time_reinvested?.other_texts} />
                <BarGroup title="What held officers back" options={a.concerns} otherTexts={a.per_question.concerns?.other_texts} />
                <BarGroup title="Keep it?" options={a.keep.distribution} />
                <BarGroup title="Time to comfort" options={a.time_to_comfort} />
                <BarGroup title="Overtime" options={a.per_question.overtime?.distribution ?? []} />
              </div>

              {data.quotes.length > 0 && (
                <div className="mt-4 rounded-xl border border-base-300 p-4">
                  <div className="flex items-baseline justify-between mb-3">
                    <h4 className="text-sm font-semibold text-base-content">In their words</h4>
                    <span className="text-[11px] text-base-content/50">{data.quotes.length} quotes</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {quotes.map((q, i) => (
                      <Reveal key={i} index={i} className="rounded-lg bg-base-200/60 px-4 py-3">
                        <p className="text-sm text-base-content/90 leading-relaxed">“{q.text}”</p>
                        <div className="mt-1.5 text-[11px] text-base-content/60">
                          — {[q.rank, q.name].filter(Boolean).join(" ") || "Officer"} · {q.agency_name}
                        </div>
                      </Reveal>
                    ))}
                  </div>
                  {data.quotes.length > 6 && (
                    <button
                      type="button"
                      onClick={() => setShowAllQuotes((v) => !v)}
                      className="mt-3 text-xs font-medium text-blue-600 dark:text-blue-300 hover:underline"
                    >
                      {showAllQuotes ? "Show fewer" : `Show all ${data.quotes.length}`}
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default SurveyResultsPanel;
